import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { EASE } from "@/components/motion/Reveal";

const IntroContext = createContext({ done: true });
export const useIntro = () => useContext(IntroContext);

const KEY = "emini-intro-seen";

export function IntroProvider({ children }) {
  const reduced = useReducedMotion();
  const [show] = useState(() => window.location.pathname === "/" && !sessionStorage.getItem(KEY) && !reduced);
  const [done, setDone] = useState(!show);

  useEffect(() => {
    if (!show) return undefined;
    document.body.style.overflow = "hidden";
    const t = setTimeout(() => {
      sessionStorage.setItem(KEY, "1");
      setDone(true);
      document.body.style.overflow = "";
    }, 3300);
    return () => clearTimeout(t);
  }, [show]);

  const value = useMemo(() => ({ done }), [done]);
  return (
    <IntroContext.Provider value={value}>
      {children}
      <AnimatePresence>{show && !done && <Intro key="intro" />}</AnimatePresence>
    </IntroContext.Provider>
  );
}

function Intro() {
  return (
    <motion.div
      data-testid="intro-overlay"
      className="fixed inset-0 z-[100] bg-ink text-paper grain flex items-center justify-center"
      initial={{ clipPath: "inset(0 0 0% 0)" }}
      exit={{ clipPath: "inset(0 0 100% 0)", transition: { duration: 1.1, ease: EASE } }}
      aria-hidden
    >
      <div className="relative z-10 w-[min(72vw,560px)]">
        <motion.div
          className="h-px bg-paper/30 origin-left mb-10"
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 1.1, ease: EASE }}
        />
        <motion.div
          className="relative"
          initial={{ clipPath: "inset(0 100% 0 0)" }}
          animate={{ clipPath: "inset(0 0% 0 0)" }}
          transition={{ duration: 1.3, ease: EASE, delay: 0.45 }}
        >
          <img src="/logo-white.png" alt="" className="w-full h-auto" />
          <motion.span
            className="absolute top-0 bottom-0 w-px bg-signal"
            initial={{ left: "0%", opacity: 1 }}
            animate={{ left: "100%", opacity: [1, 1, 0] }}
            transition={{ duration: 1.3, ease: EASE, delay: 0.45 }}
          />
        </motion.div>
        <motion.div
          className="h-px bg-paper/30 origin-right mt-10"
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 1.1, ease: EASE, delay: 0.3 }}
        />
        <motion.p
          className="label text-paper/55 mt-8 flex justify-between"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE, delay: 1.5 }}
        >
          <span>Sauber. Bis ins Detail.</span>
          <span>Deutschschweiz</span>
        </motion.p>
      </div>
    </motion.div>
  );
}
